/**
 * styles.css — Lightning Rock Winery tenant-scoped CSS.
 *
 * Injected AFTER src/styles/variables.css by init.js injectSharedStyles(), so these
 * :host(.ws-tenant-lightningrockwinery) rules override the base :host vars and the shared rich-card
 * scaffold. Moved verbatim out of variables.css (split per-winery for maintainability).
 */

/* Tenant-scoped CSS overrides go here only when a value cannot be driven
   by admin theme keys (i.e. not set inline by applyTheme() in init.js).

   Feature-card accent is one such value: it is not an admin theme key, so it
   is set per-variant here. Lighting Rock Winery cards use the house dark-red
   (#677356) for titles/CTAs, matching the old createWineListing design. */


:host(.ws-tenant-lightningrockwinery) {
  --ws-card-accent: #677356;
  --ws-card-title-font: var(--ws-engraver, var(--ws-font));
  --ws-card-desc-font: var(--ws-avenir, var(--ws-font));
}

:host(.ws-tenant-lightningrockwinery) .ws-header-logo img {
  height: 48px;
  width: auto;
  max-width: 200px;
}


:host(.ws-tenant-lightningrockwinery) .ws-banner-img {
  height: auto;
}

/* "Visit Us" banner CTA: white pill instead of the shared transparent/white-text
   default, so it reads as a button against the dark promo strip. */
:host(.ws-tenant-lightningrockwinery) .ws-banner-cta {
  background: #ffffff;
  color: var(--ws-card-accent, #677356);
  border: none;
  border-radius: 6px;
  padding: 10px 14px;
  font-size: 14px;
  font-weight: 400;
  letter-spacing: 0;
  line-height: 1.1;
  max-width: none;
  flex: 0 0 auto;
  display: inline-block;
}

:host(.ws-tenant-lightningrockwinery) .ws-banner-cta:hover {
  background: #f2f2f2;
  opacity: 1;
}

:host(.ws-tenant-lightningrockwinery) .ws-bot-bubble {
  color: #000;
}

:host(.ws-tenant-lightningrockwinery)  .ws-prompt-label {
  font-family: var(--ws-lora);
  font-size: 16px;
  font-weight: 400;
}

:host(.ws-tenant-lightningrockwinery) .ws-rich-review-button {
  border-radius: 1px;
}

:host(.ws-tenant-lightningrockwinery) .ws-card-title {
  font-family: var(--ws-monst);
}


/* Lightning Rock Winery tasting cards use a gold glow + rounded shell instead of a hard
   border (old .tasting_card: box-shadow 0 0 10px rgba(189,133,39,.4)). */
:host(.ws-tenant-lightningrockwinery) .ws-tasting-card {
  border-color: transparent;
  border-radius: 12px;
  box-shadow: 0 0 10px 0 rgba(189, 133, 39, 0.4);
}

/* Bottom-pin the action row across equal-height carousel cards so CTAs line up
   regardless of how much content sits above them (wine: price+CTA; tasting:
   footer). Base-card structure is `.ws-card > .ws-card-body > … > cta/footer`.
   SAME pattern applied per-winery on the Valle branch — candidate to promote to
   a single shared base rule when the winery branches merge into dev. */
:host(.ws-tenant-lightningrockwinery) .ws-carousel-item.ws-card {
  display: flex;
  flex-direction: column;
}
:host(.ws-tenant-lightningrockwinery) .ws-carousel-item .ws-card-body {
  flex: 1;
  display: flex;
  flex-direction: column;
}
/* wine + membership: price + CTA sink together to the card bottom */
:host(.ws-tenant-lightningrockwinery) .ws-wine-card .ws-card-price,
:host(.ws-tenant-lightningrockwinery) .ws-membership-card .ws-card-price {
  margin-top: auto;
}
/* tasting + event: the price/CTA footer pins to the bottom */
:host(.ws-tenant-lightningrockwinery) .ws-tasting-footer,
:host(.ws-tenant-lightningrockwinery) .ws-event-footer {
  margin-top: auto;
}

/* Keep "$xx" + "Per Person" on one line in the event/tasting footer. The long
   uppercase CTA ("PURCHASE TICKETS" ≈ 156px) otherwise eats the 216px footer
   row and squeezes the price block to ~50px, wrapping the unit to two lines.
   (Card is 240px — same as the old; this is just the long label, not width.)
   Stop the price block from shrinking + nowrap the unit, tighten the gap, and
   trim the CTA's footprint so both sit inline. */
:host(.ws-tenant-lightningrockwinery) .ws-event-footer,
:host(.ws-tenant-lightningrockwinery) .ws-tasting-footer {
  gap: 8px;
  margin-top: 24px;
}
:host(.ws-tenant-lightningrockwinery) .ws-event-footer .ws-price-block,
:host(.ws-tenant-lightningrockwinery) .ws-tasting-footer .ws-price-block {
  flex-shrink: 0;
}
:host(.ws-tenant-lightningrockwinery) .ws-event-footer .ws-card-unit,
:host(.ws-tenant-lightningrockwinery) .ws-tasting-footer .ws-card-unit {
  white-space: nowrap;
}
:host(.ws-tenant-lightningrockwinery) .ws-event-footer .ws-card-cta,
:host(.ws-tenant-lightningrockwinery) .ws-tasting-footer .ws-card-cta {
  padding: 10px 14px;
  font-size: 14px;
}

:host(.ws-tenant-lightningrockwinery) .ws-card-cta {
  border-radius: 20px;
}

/* Reserved events ("Weddings, Corporate & Private Events") carry no price, so
   the footer holds only the CTA. With justify-content:space-between a lone child
   sits at the LEFT — push it right (the old always rendered an empty price block
   to keep "Inquire Now" right-aligned). Harmless for priced events (already
   right via space-between). */
:host(.ws-tenant-lightningrockwinery) .ws-event-footer .ws-card-cta {
  margin-left: auto;
}

/* Membership card (Lightning Rock Winery override): match old createWineMembership /
   .card-box — uppercase serif title, centered Learn more pill, Join Now pinned
   bottom-right (.innerfooter justify-content:right). */
:host(.ws-tenant-lightningrockwinery) .ws-membership-card .ws-card-title {
  font-weight: 600;
  font-size: 16px;
  line-height: 16px;
  padding: 8px;
  margin: 0;
  white-space: pre-line; /* render the <br>→newline from the title as a break */
}
/* Old .innercardbox: column + space-between so Join Now stays at the bottom
   across equal-height carousel cards. */
:host(.ws-tenant-lightningrockwinery) .ws-lr-mem-body {
  justify-content: space-between;
  gap: 6px;
  padding: 8px;
  flex-grow: 1;
}
:host(.ws-tenant-lightningrockwinery) .ws-membership-card .ws-card-desc-full {
  font-size: 14px;
  color: #1d1d1d;
  line-height: 1.4;
}
:host(.ws-tenant-lightningrockwinery) .ws-rich-bullets li {
  display: flex;
  align-items: flex-start;
  gap: 6px;
  font-size: 14px;
  font-weight: 400;
  margin-bottom: 4px;
  color: var(--ws-text);
}

/* Old .learn-more: pill + soft maroon shadow, centered via margin:auto. */
:host(.ws-tenant-lightningrockwinery) .ws-membership-card .ws-card-learnmore {
  margin: auto;
  margin-bottom: 10px;
  padding: 8px 12px;
  border: 1px solid var(--ws-card-accent);
  border-radius: 20px;
  box-shadow: 0 4px 9.7px 0 rgba(110, 24, 17, 0.25);
  color: var(--ws-card-accent);
  font-size: 15px;
  line-height: 1;
}
/* Old .innerfooter — Join Now always bottom-right. */
:host(.ws-tenant-lightningrockwinery) .ws-lr-mem-footer {
  display: flex;
  justify-content: flex-end;
  align-items: flex-end;
  /* margin-top: 24px; */
  font-weight: 600;
}
:host(.ws-tenant-lightningrockwinery) .ws-lr-mem-cta {
  margin-top: 0;
  padding: 10px 14px;
  border: 1px solid var(--ws-card-accent);
  border-radius: 4px;
  background: var(--ws-card-accent);
  color: #ffffff;
  font-family: "Open Sans", sans-serif;
  font-size: 14px;
  font-weight: 600;
  text-decoration: none;
  cursor: pointer;
}
/* No-cover fallback: padded house logo (old defaultLogo + padding:19px). */
:host(.ws-tenant-lightningrockwinery) .ws-card-img.ws-lr-mem-logo {
  aspect-ratio: 16 / 9;
  object-fit: contain;
  padding: 19px;
  background: #ffffff;
}
/* Cover image padding like old .card-box-cover-image. */
:host(.ws-tenant-lightningrockwinery) .ws-membership-card .ws-card-head {
  padding: 6px;
}
:host(.ws-tenant-lightningrockwinery) .ws-membership-card .ws-card-img:not(.ws-lr-mem-logo) {
  aspect-ratio: 16 / 9;
  object-fit: cover;
  border-radius: 6px;
  background: transparent;
}

/* Guided-flow chips (Lightning Rock Winery) — match old .option-button: house maroon text +
   border, sized to their text (left-aligned, not full-width), tan hover, and a
   filled-maroon "selected" state on click (old changeBKG / .selected-button).
   The bolt icon uses currentColor, so it follows the text color automatically. */
/* Chip layout (indent + shrink-to-text) is shared in ChatPanel.css .ws-prompts. */
:host(.ws-tenant-lightningrockwinery) .ws-prompt-chip {
  color: var(--ws-card-accent);
  border-color: var(--ws-card-accent);
  font-family: "Avenir", sans-serif;
  font-weight: 600;
  border-radius: 0;
}
:host(.ws-tenant-lightningrockwinery) .ws-prompt-chip:hover {
  background: var(--ws-card-accent);
  color: #fff;
}
:host(.ws-tenant-lightningrockwinery) .ws-prompt-chip.is-selected {
  background: var(--ws-card-accent);
  border-color: var(--ws-card-accent);
  color: #ffffff;
}
/* The bolt icon span pins its own color to --ws-primary (ChatPanel.css), which
   overrides the white text on a selected chip and leaves the icon invisible on
   the filled background. Let the icon follow the chip's text color when selected. */
:host(.ws-tenant-lightningrockwinery) .ws-prompt-chip.is-selected .ws-prompt-icon {
  color: inherit;
}

/* ── Lightning Rock Winery wine listing card (parity: createWineListing) ────────── */
:host(.ws-tenant-lightningrockwinery) .ws-lr-winecard {
}
:host(.ws-tenant-lightningrockwinery) .ws-lr-winecard-head {
  position: relative;
  height: 180px;
  min-height: 180px;
  max-height: 180px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

:host(.ws-tenant-lightningrockwinery) .ws-lr-winecard-img {
  width: 100%;
  height: 100%;
  object-fit: contain;
  cursor: pointer;
}

:host(.ws-tenant-lightningrockwinery) .ws-lr-winecard-soldout {
  position: absolute;
  right: 8px;
  top: 8px;
  text-align: center;
  padding: 2px 8px;
  font-size: 12px;
  font-weight: 600;
  border-radius: 3px;
  width: fit-content;
  background-color: #000000ab;
  color: #fff;
}

:host(.ws-tenant-lightningrockwinery) .ws-lr-winecard-info {
  margin-top: 12px;
}

:host(.ws-tenant-lightningrockwinery) .ws-lr-winecard-title {
  margin: 0 0 4px;
  font-size: 16px;
  font-weight: 600;
  font-family: var(--ws-monst);
  color: var(--ws-card-accent, #6e1811);
  line-height: normal;
  min-height: 40px;
  display: -webkit-box;
  -webkit-line-clamp: 4;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
}

:host(.ws-tenant-lightningrockwinery) .ws-lr-winecard-meta,
:host(.ws-tenant-lightningrockwinery) .ws-lr-winecard-desc {
  margin: 0;
  font-family: var(--ws-card-desc-font);
  font-size: 15px;
  font-weight: 600;
  line-height: 120%;
  color: #1d1d1d;
}

:host(.ws-tenant-lightningrockwinery) .ws-card-meta,
:host(.ws-tenant-lightningrockwinery) .ws-card-desc {
  font-family: var(--ws-card-desc-font);
  font-size: 14px;
  font-weight: 400;
  color: var(--ws-text);
}


:host(.ws-tenant-lightningrockwinery) .ws-lr-winecard-meta {
  margin: 0;
  font-family: var(--ws-card-desc-font);
  font-size: 14px;
  font-weight: 400;
  color: #1d1d1d;
}

:host(.ws-tenant-lightningrockwinery) .ws-lr-winecard-desc-light {
  font-weight: 400;
}
:host(.ws-tenant-lightningrockwinery) .ws-lr-winecard-body {
  margin-top: 10px;
}

:host(.ws-tenant-lightningrockwinery) .ws-lr-winecard-tasting {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 5px;
  padding: 8px 12px;
  margin: auto;
  margin-bottom: 10px;
  border: 1px solid var(--ws-card-accent, #6e1811);
  border-radius: 20px;
  background: #fff;
  box-shadow: 0 4px 9.7px 0 rgba(110, 24, 17, 0.25);
  color: var(--ws-card-accent, #6e1811);
  font-size: 15px;
  line-height: 1;
  cursor: pointer;
}
.ws-lr-tasting svg {
  flex: 0 0 auto;
}

:host(.ws-tenant-lightningrockwinery) .ws-lr-winecard-product-details {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 12px;
  width: 100%;
  margin-top: auto;
  padding-top: 12px;
  font-weight: 600;
  flex-shrink: 0;
}

.ws-lr-price {
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: var(--ws-card-accent, #6e1811);
}
.ws-lr-unit {
  margin: 0;
  font-size: 12px;
  font-weight: 400;
  color: #000;
}
.ws-lr-add {
  width: 100%;
  padding: 10px 12px;
  border: none;
  border-radius: 4px;
  background: var(--ws-card-accent, #6e1811);
  color: #fff;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
}

.ws-lr-action {
  display: flex;
  align-items: center;
  gap: 10px;
}
.ws-lr-delete {
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border: 1px solid var(--ws-border);
  border-radius: 4px;
  background: #fff;
  color: #0a0a0a;
  cursor: pointer;
}
.ws-lr-qty {
  flex: 1 1 auto;
  height: 40px;
  /* Right padding leaves room for the custom chevron so the value + icon
     never crowd each other (native appearance gave no controllable spacing). */
  padding: 0 34px 0 12px;
  border: 1px solid var(--ws-border);
  border-radius: 4px;
  background-color: #fff;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%23574D39' stroke-width='2.5' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='M6 9l6 6 6-6'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 12px center;
  background-size: 12px;
  color: var(--ws-text);
  font-size: 14px;
  appearance: none;
  -webkit-appearance: none;
  -moz-appearance: none;
  cursor: pointer;
}
.ws-lr-update {
  flex: 0 0 auto;
  padding: 0 16px;
  height: 40px;
  border: none;
  border-radius: 4px;
  background: var(--ws-card-accent, #6e1811);
  color: #fff;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
}

:host(.ws-tenant-lightningrockwinery) .ws-lr-winecard-product-footer {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 10px;
  width: 100%;
}

:host(.ws-tenant-lightningrockwinery) .ws-lr-winecard-product-footer p:nth-child(2),
:host(.ws-tenant-lightningrockwinery) .ws-lr-winecard-product-footer p:nth-child(3),
:host(.ws-tenant-lightningrockwinery) .ws-lr-winecard-product-footer p:nth-child(2),
:host(.ws-tenant-lightningrockwinery) .ws-lr-winecard-product-footer p:nth-child(3) {
  font-size: 0.75rem;
  color: var(--ws-text, #000);
  margin-block: 0;
  font-weight: 400;
}



/* Qty controls — old .product-action parity (ss2) */
:host(.ws-tenant-lightningrockwinery) .ws-lr-product-action,
:host(.ws-tenant-lightningrockwinery) .ws-lr-card .product-action {
  display: flex;
  align-items: stretch;
  gap: 10px;
  margin-left: auto;
  white-space: nowrap;
  width: auto;
}

:host(.ws-tenant-lightningrockwinery) .ws-lr-qty-wrap,
:host(.ws-tenant-lightningrockwinery) .ws-lr-card .product-quantity-wrapper {
  display: flex;
  align-items: center;
  gap: 4px;
  margin-left: 0;
}

:host(.ws-tenant-lightningrockwinery) .ws-lr-card .product-action button {
  appearance: none;
  -webkit-appearance: none;
  box-sizing: border-box;
  cursor: pointer;
  display: flex;
  justify-content: center;
  align-items: center;
  width: 30px;
  min-width: 30px;
  max-width: 30px;
  height: 30px;
  min-height: 30px;
  padding: 0 !important;
  border-radius: 2px;
  border: 1px solid #e5e5e5;
  background: #f1f1f1;
  box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05);
  color: #0a0a0a;
  font-size: 16px;
  font-weight: 500;
  line-height: 1;
  text-transform: none;
}

:host(.ws-tenant-lightningrockwinery) .ws-lr-delete svg,
:host(.ws-tenant-lightningrockwinery) .ws-lr-card .delete-cart svg {
  width: 16px;
  height: 16px;
  display: block;
}

:host(.ws-tenant-lightningrockwinery) .ws-lr-cart-qty,
:host(.ws-tenant-lightningrockwinery) .ws-lr-card .cart-qty {
  box-sizing: border-box;
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 24px;
  height: 30px;
  padding: 2px 10px;
  margin: 0;
  border: none;
  background: transparent;
  box-shadow: none;
  color: #0a0a0a;
  font-size: 14px;
  font-weight: 600;
  line-height: 1.5;
  text-align: center;
}

:host(.ws-tenant-lightningrockwinery) .ws-lr-view-basket {
  border-color: var(--ws-primary);
  color: var(--ws-primary);
  flex: 0 0 auto;
  width: max-content;
}

:host(.ws-tenant-lightningrockwinery) .ws-lr-view-basket:hover {
  background: color-mix(in srgb, var(--ws-primary) 8%, transparent);
}



/* Event Card */
:host(.ws-tenant-lightningrockwinery) .ws-tasting-card .ws-card-body .ws-card-title,
:host(.ws-tenant-lightningrockwinery) .ws-event-card .ws-card-body .ws-card-title {
  font-size: 16px;
  font-weight: 600;
}

:host(.ws-tenant-lightningrockwinery) .ws-event-card .ws-card-head {
  max-height: 180px;
}

:host(.ws-tenant-lightningrockwinery) .ws-event-card .ws-card-img {
  aspect-ratio: 16/9;
  object-fit: cover;
}


/* Tasting Notes Drawer */

:host(.ws-tenant-lightningrockwinery) .ws-drawer-ingredient-bullet {
  color: var(--ws-card-accent);
}

:host(.ws-tenant-lightningrockwinery) .ws-wine-drawer-intro,
:host(.ws-tenant-lightningrockwinery) .ws-drawer-pairing-box,
:host(.ws-tenant-lightningrockwinery) .ws-drawer-fp-card {
  background: #f0f3ec;
}
